import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Infinity, Sparkles, Tag } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

interface Package {
  id: string;
  key: string;
  name: string;
  description?: string;
  priceMonthly: number;
  priceYearly: number;
  priceMonthlyOriginal?: number;
  priceYearlyOriginal?: number;
  discount?: {
    name: string;
    badgeLabel?: string | null;
    type: "PERCENTAGE" | "FIXED";
    value: number;
  } | null;
  maxInvoices: number | null;
  maxQuotes: number | null;
  maxExpenses: number | null;
  maxContacts: number | null;
  maxUsers: number | null;
  freeLayoutTemplates: number | null;
  hasOcr: boolean;
  hasVatReport: boolean;
  hasBankLink: boolean;
  ocrCreditsPerMonth: number;
  isPopular?: boolean;
}

function fmtLimit(n: number | null) {
  if (n === null) return "onbeperkt";
  return n.toString();
}

export default function Packages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<Package[]>([]);
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");

  useEffect(() => {
    api.get("/api/packages").then((res) => setPackages(res.data));
  }, []);

  const choose = (id: string) => {
    if (user) {
      navigate(`/onboarding?package=${id}`);
    } else {
      navigate(`/register?package=${id}`);
    }
  };

  return (
    <div className="page-gradient py-20 px-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 left-1/3 w-[600px] h-[600px] rounded-full bg-brand-300/25 blur-3xl animate-liquid-sway" />
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-indigo-300/25 blur-3xl animate-liquid-sway" style={{ animationDelay: "-2s" }} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-14">
          <Link to="/" className="text-brand-600 font-bold hover:underline">
            ← Terug naar home
          </Link>
          <h1 className="text-4xl font-bold mt-5 mb-4">Kies je AIBoek-pakket</h1>
          <p className="text-slate-500">Geen verborgen kosten. Altijd opzegbaar. Excl. btw.</p>

          <div className="mt-8 inline-flex items-center bg-white/70 backdrop-blur rounded-full p-1.5 shadow-soft border border-slate-200/60">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${
                billing === "monthly"
                  ? "bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-500/25"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Maandelijks
            </button>
            <button
              onClick={() => setBilling("yearly")}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${
                billing === "yearly"
                  ? "bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-500/25"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Jaarlijks
            </button>
          </div>
        </div>

        <div className="flex flex-wrap justify-center items-stretch gap-8">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="relative w-full sm:w-[360px] lg:w-[400px]"
            >
              {pkg.discount && (
                <div className="absolute -top-3 left-3 z-10">
                  <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-amber-500/25">
                    <Tag size={20} /> {pkg.discount.badgeLabel || "Actie"}
                  </span>
                </div>
              )}
              {pkg.isPopular && (
                <div className="absolute -top-3 right-3 z-10">
                  <span className="inline-flex items-center gap-1 bg-gradient-to-r from-brand-500 to-brand-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg shadow-brand-500/25">
                    <Sparkles size={21} /> Populair
                  </span>
                </div>
              )}
              <Card
                className={`relative p-6 h-full flex flex-col surface-card hover:-translate-y-1 transition-transform ${
                  pkg.isPopular ? "ring-2 ring-brand-500" : ""
                } ${pkg.discount ? "ring-2 ring-amber-400" : ""}`}
              >
                <h3 className="text-xl font-bold">{pkg.name}</h3>
                <p className="text-slate-500 text-sm mt-2 min-h-[40px]">{pkg.description}</p>
                <div className="my-5">
                  {(() => {
                    const price = billing === "monthly" ? pkg.priceMonthly : pkg.priceYearly;
                    const original =
                      billing === "monthly" ? pkg.priceMonthlyOriginal : pkg.priceYearlyOriginal;
                    const hasDiscount = !!pkg.discount && original != null && original > price;
                    return (
                      <div className="flex flex-col">
                        {hasDiscount && (
                          <span className="text-sm text-slate-400 line-through">
                            €{Number(original).toFixed(2)}
                          </span>
                        )}
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-extrabold text-gradient">
                            €{Number(price).toFixed(2)}
                          </span>
                          <span className="text-slate-500 text-sm">
                            /{billing === "monthly" ? "maand" : "jaar"}
                          </span>
                        </div>
                        {hasDiscount && (
                          <span className="mt-1 text-xs font-bold text-amber-600">
                            {pkg.discount!.type === "PERCENTAGE"
                              ? `${pkg.discount!.value}% korting`
                              : `€${pkg.discount!.value} voordeel`}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <ul className="space-y-2.5 mb-6 flex-1 text-sm">
                  {[
                    { label: "facturen/maand", value: pkg.maxInvoices },
                    { label: "offertes/maand", value: pkg.maxQuotes },
                    { label: "uitgaven/maand", value: pkg.maxExpenses },
                    { label: "contacten", value: pkg.maxContacts },
                    { label: "gebruiker(s)", value: pkg.maxUsers },
                  ]
                    // Bij pakketten mét OCR toont de "uitgaven-bon/maand lezen"-
                    // regel (hieronder) al het uitgaven-aantal; voorkom dubbele
                    // "uitgaven/maand"-regel.
                    .filter((item) => !(item.label === "uitgaven/maand" && pkg.hasOcr))
                  .map((item) => (
                    <li key={item.label} className="flex items-center gap-2 text-slate-600">
                      <Check size={27} className="text-brand-500 shrink-0" />
                      <span className="flex items-center gap-1">
                        {item.value === null && <Infinity size={21} />}
                        {fmtLimit(item.value)} {item.label}
                      </span>
                    </li>
                  ))}
                  {pkg.hasOcr && (
                    <li className="flex items-center gap-2 text-slate-600">
                      <Check size={27} className="text-brand-500 shrink-0" />
                      <span>{pkg.ocrCreditsPerMonth} uitgaven-bon/maand lezen</span>
                    </li>
                  )}
                  {pkg.hasVatReport && (
                    <li className="flex items-center gap-2 text-slate-600">
                      <Check size={27} className="text-brand-500 shrink-0" />
                      <span>BTW-overzicht</span>
                    </li>
                  )}
                  {pkg.freeLayoutTemplates != null && pkg.freeLayoutTemplates > 0 && (
                    <li className="flex items-center gap-2 text-slate-600">
                      <Check size={27} className="text-brand-500 shrink-0" />
                      <span className="flex items-center gap-1">
                        {pkg.freeLayoutTemplates === null && <Infinity size={21} />}
                        {fmtLimit(pkg.freeLayoutTemplates)} gratis lay-outtemplates
                      </span>
                    </li>
                  )}
                </ul>

                <Button
                  variant={pkg.isPopular ? "primary" : "outline"}
                  className="w-full"
                  onClick={() => choose(pkg.id)}
                >
                  Kies {pkg.name}
                </Button>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
